import { STORAGE_KEYS, storage } from '@/lib/storage/keys';

export function accessToken(): string | null {
  return storage.get(STORAGE_KEYS.accessToken);
}

export function authHeaders(token: string | null = accessToken()): Record<string, string> {
  return token === null ? {} : { Authorization: `Bearer ${token}` };
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
  }
}

interface ErrorBody {
  readonly error?: string;
  readonly errors?: readonly string[];
}

function messageFrom(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null) {
    const { error, errors } = body as ErrorBody;
    if (typeof error === 'string' && error !== '') return error;
    if (Array.isArray(errors) && errors.length > 0) return errors.join('. ');
  }
  return fallback;
}

/** JSON request against the site's own /api. Non-2xx responses and `success:false` both throw ApiError. */
export async function apiJson<T>(
  path: string,
  init: { method?: 'GET' | 'POST'; body?: unknown; auth?: boolean; fallbackError?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = init.auth === true ? authHeaders() : {};
  const request: RequestInit = { method: init.method ?? 'GET', headers, cache: 'no-store' };
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    request.body = JSON.stringify(init.body);
  }
  const response = await fetch(path, request);
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const fallback = init.fallbackError ?? `Request failed (${response.status})`;
  if (!response.ok) throw new ApiError(messageFrom(body, fallback), response.status, body);
  if (typeof body === 'object' && body !== null && (body as { success?: unknown }).success === false) {
    throw new ApiError(messageFrom(body, fallback), response.status, body);
  }
  return body as T;
}
